import { UiElement } from '../ui/UiElement.js'

/**
 * Represents a basic plot configuration using Plotly.
 * @extends UiElement 
 * @memberof vef.plot
 */
class PlotlyBasicPlotConfig extends UiElement {

    /**
     * Constructs a new PlotlyBasicPlotConfig object.
     * @param {HTMLElement} graphDiv - The HTML element representing the graph container.
     * @param {Array} [data=[]] - The data to be plotted.
     * @param {Object} [layout={}] - The layout configuration for the plot.
     * @param {Object} [config={}] - The configuration options for the plot.
     */
    constructor(graphDiv, data = [], layout = {}, config = {}) {
        super(graphDiv);

        // Default values for data, layout, and config
        const dataVefDefault = [];
        const layoutVefDefault = {
            margin: {
                l: 60,
                r: 10,
                b: 50,
                t: 80,
                pad: 0
            },
        };
        const configVefDefault = {
            displayModeBar: true,
            displaylogo: false,
            responsive: true,
        };

        // Merge provided data, layout, and config with defaults
        this.graphDiv = this.getElement();
        this.data = _.merge(dataVefDefault, data);
        this.layout = _.merge(layoutVefDefault, layout);
        this.config = _.merge(configVefDefault, config);

        this.graphDiv.classList.add("vef-plot");
    }

    /**
     * Dispose of the plot by purging Plotly and calling the base class's dispose method.
     */
    dispose() {
        Plotly.purge(this.graphDiv);
        super.dispose();
    }
}

/**
 * Represents a plot configuration using Plotly with additional features for measurements.
 * @extends PlotlyBasicPlotConfig
 * @memberof vef.plot
 * @private
 */
class PlotlyVefPlotConfig extends PlotlyBasicPlotConfig {

    /**
     * Constructs a new PlotlyVefPlotConfig object.
     * @param {HTMLElement} graphDiv - The HTML element representing the graph container.
     * @param {Array} [data=[]] - The data to be plotted.
     * @param {Object} [layout={}] - The layout configuration for the plot.
     * @param {Object} [config={}] - The configuration options for the plot.
     */
    constructor(graphDiv, data = [], layout = {}, config = {}) {
        // Default values for data, layout, and config specific to measurements
        const dataMeasurementsDefault = [];
        const layoutMeasurementsDefault = {
            spikedistance: -1,
            xaxis: { showspikes: true, spikethickness: 2, autorange: true },
            yaxis: { showspikes: true, spikethickness: 2, autorange: true },
        };
        const configMeasurementsDefault = {
            modeBarButtonsToRemove: ["select2d", "lasso2d", "hoverClosestGl2d", "hoverClosestPie", "toggleHover", "sendDataToCloud", "toggleSpikelines", "hoverClosestCartesian", "hoverCompareCartesian", "autoScale2d"],
        };

        // Create a PlotlyChangeModeButton object
        const modeButton = new PlotlyChangeModeButton(data);

        // Merge provided data, layout, and config with defaults and mode button config
        const dataPreMerged = _.merge(dataMeasurementsDefault, data);
        const layoutPreMerged = _.merge(layoutMeasurementsDefault, layout);
        const configPreMerged = _.merge(modeButton.configDefault, configMeasurementsDefault, config);

        super(graphDiv, dataPreMerged, layoutPreMerged, configPreMerged);

        this.graphDiv.classList.add("vef-plot-measurements");
    }
}

class PlotlyChangeModeButton {
    constructor(data) {
        this.modeIndex = 0;
        this.plotModes = ["markers", "lines", "lines+markers"]

        for (let i = 0; i < data.length; ++i) {
            if (this.modeIndex >= this.plotModes.length) this.modeIndex = 0;
            data[i].mode = this.plotModes[this.modeIndex];
            data[i].type = "scattergl";
        }

        this.configDefault = {
            modeBarButtonsToAdd: [
                {
                    name: 'Change Mode',
                    icon: () => {
                        const icon = document.createElement("i");
                        icon.classList.add("fas", "fa-project-diagram");
                        return icon;
                    },
                    click: gd => {
                        this.modeIndex = (this.modeIndex + 1) % this.plotModes.length;
                        Plotly.restyle(gd, 'mode', this.plotModes[this.modeIndex])
                    }
                }
            ]
        };
    }
}


/**
 * Represents a plot configuration using Plotly with additional features for filtering.
 * @extends PlotlyVefPlotConfig
 * @memberof vef.plot
 */
class PlotlyFilterVefPlotConfig extends PlotlyVefPlotConfig {

    /**
     * Constructs a new PlotlyFilterVefPlotConfig object.
     * @param {HTMLElement} graphDiv - The HTML element representing the graph container.
     * @param {Array} [data=[]] - The data to be plotted.
     * @param {Object} [layout={}] - The layout configuration for the plot.
     * @param {Object} [config={}] - The configuration options for the plot.
     */
    constructor(graphDiv, data = [], layout = {}, config = {}) {
        // Default values for data, layout, and config specific to measurements
        const dataMeasurementsDefault = [];
        const layoutMeasurementsDefault = {};
        const configMeasurementsDefault = {
            modeBarButtonsToRemove: ["lasso2d", "hoverClosestGl2d", "hoverClosestPie", "toggleHover", "sendDataToCloud", "toggleSpikelines", "hoverClosestCartesian", "hoverCompareCartesian", "autoScale2d"],
        };

        // Merge provided data, layout, and config with defaults
        const dataPreMerged = _.merge(dataMeasurementsDefault, data);
        const layoutPreMerged = _.merge(layoutMeasurementsDefault, layout);
        const configPreMerged = _.merge(configMeasurementsDefault, config);

        super(graphDiv, dataPreMerged, layoutPreMerged, configPreMerged);

        this.graphDiv.classList.add("vef-plot-filter");
    }

    /**
     * Filter the data based on the selected points on the plot.
     */
    filterData() {
        this.getElement().on('plotly_selected', function (eventData) {
            if (!eventData) return
            var x = [];
            var y = [];

            eventData.points.forEach(function (pt) {
                x.push(pt.x);
                y.push(pt.y);
            });
            console.log('selected', x, y);
        });
    }
}

export { PlotlyFilterVefPlotConfig, PlotlyVefPlotConfig, PlotlyBasicPlotConfig };